
import React, { useState, useEffect } from 'react';
import { APP_TITLE, ICONS } from './constants'; 
import NavigationDropdown from './NavigationDropdown';
import type { Page, NavParams, UserProfile, SubscriptionTier } from '@types';
import { auth } from '../../firebaseConfig';
import { User } from 'firebase/auth';
import { openAccountSecurity } from '../common/mfa';
import { isOrganizationAccess } from '../common/organizationAccessMode';
import { ArrowPathIcon, ShieldExclamationIcon } from '@heroicons/react/24/outline';

// This component contains the logic and UI for the developer refresh button.
const DevManualRefresh: React.FC<{ currentUser: UserProfile | null }> = ({ currentUser }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [feedback, setFeedback] = useState('');
    
    const developerEmail = "johnferg77@gmail.com"; // The specific developer email

    const handleManualRefresh = async () => {
        setIsLoading(true);
        setFeedback('Invoking analysis agent...');
        try {
            const projectId = import.meta.env.VITE_FIREBASE_PROJECT_ID || 'inteloptics-dev';
            const functionUrl = `https://us-central1-${projectId}.cloudfunctions.net/manualCountryAnalysisTrigger`;
            const response = await fetch(functionUrl, { 
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
            });
            
            if (!response.ok) throw new Error(`Function call failed with status ${response.status}`);

            const result = await response.json();
            setFeedback(result.message || 'Refresh initiated.');
        } catch (error) {
            console.error('Manual refresh failed:', error);
            setFeedback(`Error: ${(error as Error).message}`);
        } finally {
            setIsLoading(false);
            setTimeout(() => setFeedback(''), 8000);
        }
    };

    // Only render the button if the logged-in user's email matches the developer email.
    if (currentUser?.email !== developerEmail) {
        return null;
    }

    return (
        <div className="relative">
            <button onClick={handleManualRefresh} disabled={isLoading} className="bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xs py-2 px-3 rounded-lg shadow flex items-center">
                {isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin mr-2" /> : <ShieldExclamationIcon className="h-4 w-4 mr-2" />}
                <span>{isLoading ? 'Refreshing...' : 'Dev Refresh'}</span>
            </button>
            {feedback && <p className="absolute top-full mt-2 w-60 text-xs text-slate-700 bg-white p-2 rounded shadow-lg border">{feedback}</p>}
        </div>
    );
};


interface HeaderProps {
    onNavigate: (navigation: { page: Page, params?: NavParams }) => void;
    currentUser: UserProfile | null; // Changed to pass the full user object
    tier?: SubscriptionTier | 'pending';
    onLogout: () => void;
    onNavigateHome: () => void;
    currentIndustry: string | null;
    isNavIndicatorVisible: boolean;
    onToggleNavIndicator: () => void;
    onOpenGlossary: () => void;
    fullWorkspaceAccess?: boolean;
    navigationLocked?: boolean;
}

const Header: React.FC<HeaderProps> = ({ 
    onNavigate, 
    currentUser,
    tier,
    onLogout, 
    onNavigateHome,
    currentIndustry,
    isNavIndicatorVisible,
    onToggleNavIndicator,
    onOpenGlossary,
    fullWorkspaceAccess = false,
    navigationLocked = false
}) => {

    const userItems = [
        ...(!isOrganizationAccess ? [{ title: 'Account security', action: openAccountSecurity }] : []),
        { title: 'Logout', action: onLogout },
    ];

    return (
        <header className="bg-white border-b border-slate-200 p-4 flex justify-between items-center z-50 text-slate-800">
            <div className="flex items-center">
                {!navigationLocked && <button onClick={onToggleNavIndicator} className="p-2 rounded-full hover:bg-slate-100 mr-2">
                    {isNavIndicatorVisible ? ICONS.menuClose : ICONS.menu}
                </button>}
                <button onClick={onNavigateHome} className="text-xl font-bold text-slate-900 flex items-center">
                    {ICONS.logo}
                    <span className="ml-2">{APP_TITLE}</span>
                </button>
                {currentIndustry && (
                    <div className="hidden md:flex items-center ml-6">
                        <span className="text-slate-400">|</span>
                        <span className="ml-4 text-sm font-semibold text-slate-600">{currentIndustry}</span>
                    </div>
                )}
            </div>
            <div className="flex items-center gap-4">
                {fullWorkspaceAccess && <DevManualRefresh currentUser={currentUser} />}
                {fullWorkspaceAccess && <button
                    onClick={onOpenGlossary} 
                    className="p-2 rounded-full hover:bg-slate-100 flex items-center gap-2 group transition-all"
                    title="Terminology Index"
                >
                    <span className="text-xs font-bold text-slate-400 group-hover:text-slate-600 hidden lg:block">INDEX</span>
                    {ICONS.search || <svg className="h-5 w-5 text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>}
                </button>}
                <NavigationDropdown 
                    title={currentUser?.displayName || 'User'}
                    items={userItems}
                    onNavigate={onNavigate}
                />
            </div>
        </header>
    );
};

export default Header;
